import React, { useEffect, useState, useCallback } from "react";
import { apiGet } from "@/lib/api";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Stats from "@/components/Stats";
import KeyStats from "@/components/KeyStats";
import Method from "@/components/Method";
import About from "@/components/About";
import Archive from "@/components/Archive";
import Pricing from "@/components/Pricing";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import FreeDailyTip from "@/components/FreeDailyTip";
import { Loader2, RefreshCw } from "lucide-react";

export default function Home() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    const [sR, setR, mR, fR] = await Promise.allSettled([
      apiGet("/stats"),
      apiGet("/settings"),
      apiGet("/matches"),
      apiGet("/free-tips"),
    ]);

    const settings = setR.status === "fulfilled" ? setR.value.data : null;
    const stats = sR.status === "fulfilled" ? sR.value.data : null;
    const matches = mR.status === "fulfilled" ? mR.value.data : [];
    const freeTips = fR.status === "fulfilled" ? fR.value.data : [];

    // settings + stats are essential for the page shell; matches can degrade to [].
    if (!settings || !stats) {
      console.error("Home load failed", { settings: !!settings, stats: !!stats });
      setData({ error: true });
      return;
    }
    setData({ stats, settings, matches, freeTips });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (data && data.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 gap-5" style={{ background: "var(--at-bg)" }}>
        <div className="font-heading text-2xl font-extrabold tracking-tight text-white">
          Austria <span className="text-[var(--trust-gold)]">Tips</span>
        </div>
        <p className="text-white/80 font-body max-w-sm leading-relaxed">
          Couldn't load the latest data. Please check your connection and try again.
          <br />
          <span className="text-white/60 text-sm">
            Die Daten konnten nicht geladen werden. Bitte versuche es erneut.
          </span>
        </p>
        <button
          onClick={load}
          data-testid="home-retry-btn"
          className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-[#8C0A20] font-heading font-semibold shadow-lg transition-transform hover:-translate-y-0.5 active:translate-y-0"
        >
          <RefreshCw size={18} />
          Try again / Erneut versuchen
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--at-bg)" }}>
        <Loader2 className="animate-spin text-white" size={36} />
      </div>
    );
  }

  return (
    <div style={{ background: "var(--at-bg)" }}>
      <Header />
      <main>
        <Hero stats={data.stats} settings={data.settings} />
        <Stats stats={data.stats} settings={data.settings} />
        <Method />
        <About />
        <KeyStats stats={data.stats} settings={data.settings} />
        <FreeDailyTip tips={data.freeTips} />
        <Archive matches={data.matches} />
        <Pricing settings={data.settings} />
        <Contact />
      </main>
      <Footer settings={data.settings} />
    </div>
  );
}
